<template>
  <div class="answers-container">
    
    <!-- Loader -->
    <div v-if="loadingAnswers" class="d-flex justify-center py-8">
      <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
    </div>

    <!-- Empty State -->
    <div v-if="answers.length == 0 && !loadingAnswers" class="text-center py-10 px-4 rounded-xl mx-2 bg-grey-lighten-5 mt-4" style="border: 1px solid #f0f0f0;">
      <v-icon style="font-size: 28px; color: #BDBDBD; margin-bottom: 12px;">fa:fa-regular fa-comments</v-icon>
      <div class="text-body-2 font-weight-medium text-grey-darken-2">No hay respuestas aún</div>
      <div class="text-caption text-medium-emphasis">Sé el primero en comentar.</div>
    </div>

    <!-- Lista de Comentarios (Hilo moderno / Activity feed) -->
    <div v-else class="comments-list mt-6 px-1">
      <div v-for="(answ, index) in answers" :key="index" class="comment-item d-flex mb-4 position-relative group">
        
        <!-- Línea conectora visual (Timeline) - Oculta en móvil -->
        <div v-if="index < answers.length - 1" class="connector-line"></div>

        <!-- Avatar - Más pequeño en móvil -->
        <div class="avatar-wrapper flex-shrink-0 z-index-1">
          <v-avatar :size="$vuetify.display.smAndDown ? 32 : 42" class="bg-white elevation-1" style="border: 1px solid #EEF2F7;">
            <img v-if="answ.user.avatar" :src="answ.user.avatar" alt="Avatar" style="object-fit: cover; width: 100%; height: 100%;">
            <v-img v-else :src="require('@/assets/' + answ.user.tipouser.toLowerCase() + '.png')" cover></v-img>
          </v-avatar>
        </div>

        <!-- Bloque de Contenido (Tarjeta moderna) -->
        <div class="comment-card flex-grow-1 position-relative"
             :class="{'comment-card--mine': isMyID(answ.user_id)}">
          
          <!-- Indicador superior (acento) -->
          <div class="accent-bar" :style="isMyID(answ.user_id) ? 'background: linear-gradient(90deg, #002B49 0%, #005691 100%);' : 'background: linear-gradient(90deg, #E5E7EB 0%, #F3F4F6 100%);'"></div>

          <!-- Header -->
          <div class="comment-header">
            <div class="comment-header__user">
              <div class="d-flex align-center flex-wrap gap-1">
                <span class="comment-header__name">
                  {{ capitalizeWords(oneName(answ.user.nombres) + ' ' + oneName(answ.user.apellidos)) }}
                </span>

                <v-chip v-if="answ.user.tipouser === 'admin' || answ.user.tipouser === 'agente'"
                  size="x-small" variant="tonal" label style="background-color: rgba(0, 86, 145, 0.12); color: #005691; font-weight: 700;">
                  Equipo
                </v-chip>
                <v-chip v-else-if="isMyID(answ.user_id)"
                  size="x-small" variant="tonal" label style="background-color: rgba(17, 24, 39, 0.08); color: #111827; font-weight: 700;">
                  Tú
                </v-chip>
              </div>

              <!-- Email - Oculto en móvil -->
              <div class="comment-header__email hidden-xs">
                {{ answ.user.email }}
              </div>
            </div>

            <!-- Fecha y acciones -->
            <div class="comment-header__actions">
              <div class="comment-header__date">
                {{ fechasmall($vuetify.display.smAndDown ? "DD MMM" : "DD MMM, HH:mm", answ.created_at) }}
              </div>

              <!-- Acciones - Visible en mobile también -->
              <div class="comment-header__delete" v-if="!isRoleVisitor() && isMyID(answ.user_id)">
                 <v-btn icon variant="text" size="small" density="compact" color="grey-darken-1" 
                    @click="deleteAnswer({ loading: true, idticket: answ.ticket_id, idanswer: answ.idanswer })">
                    <v-icon style="font-size: 14px; color: #6B7280;">fa:fa-solid fa-trash-can</v-icon>
                 </v-btn>
              </div>
            </div>
          </div>

          <!-- Cuerpo -->
          <div class="comment-body text-body-2 text-grey-darken-3 caja-texto-styles"
               v-html="answ.message" style="line-height: 1.65;"></div>

          <!-- Adjuntos -->
          <div v-if="answ.file && answ.file.length > 0" class="mt-4 pt-4" style="border-top: 1px solid #F3F4F6;">
            <div class="d-flex align-center mb-3">
              <v-icon style="font-size: 14px; color: #6B7280; margin-right: 8px;">fa:fa-solid fa-paperclip</v-icon>
              <span class="text-body-2 font-weight-bold text-grey-darken-2">Archivos Adjuntos ({{ answ.file.length }})</span>
            </div>
            <div class="attachments-grid">
              <div v-for="(f, i) in answ.file" :key="i" 
                class="attachment-card"
                @click="onOpenDialogAdjunto(f)">
                
                <!-- Icono del archivo -->
                <div class="attachment-card__icon-wrapper">
                  <v-avatar size="48" class="attachment-card__icon" rounded="lg">
                    <v-img 
                      :src="require('@/assets/' + iconExtension(getFileNameFromURL(f)['extension']))" 
                      cover
                    ></v-img>
                  </v-avatar>
                </div>

                <!-- Info del archivo -->
                <div class="attachment-card__info">
                  <div class="attachment-card__name">
                    {{ getFileNameFromURL(f) ? getFileNameFromURL(f)['name'] : 'Archivo' }}
                  </div>
                  <div class="attachment-card__extension">
                    {{ getFileNameFromURL(f)['extension']?.toUpperCase() || 'FILE' }}
                  </div>
                </div>

                <!-- Icono de acción -->
                <div class="attachment-card__action">
                  <v-icon style="font-size: 16px; color: #005691;">fa:fa-solid fa-arrow-up-right-from-square</v-icon>
                </div>
              </div>
            </div>
          </div>

          <!-- CC -->
          <div v-if="answ.answerCopies && answ.answerCopies.length > 0" class="mt-3 pt-3" style="border-top: 1px solid #F8FAFC;">
            <div class="d-flex align-center mb-2">
              <v-icon style="font-size: 12px; color: #6B7280; margin-right: 8px;">fa:fa-solid fa-at</v-icon>
              <span class="text-caption font-weight-bold text-grey-darken-2">Copias (CC)</span>
            </div>
            <div class="d-flex flex-wrap gap-2">
              <v-tooltip v-for="(cc, i) in answ.answerCopies" :key="i" location="top" :text="cc.email">
                <template v-slot:activator="{ props }">
                  <div v-bind="props" class="cc-pill">
                    <v-icon style="font-size: 12px; color: #005691; margin-right: 8px;">fa:fa-regular fa-envelope</v-icon>
                    <span class="text-caption font-weight-medium text-truncate" style="max-width: 220px;">
                      {{ cc.email }}
                    </span>
                  </div>
                </template>
              </v-tooltip>
            </div>
          </div>

        </div>
      </div>
    </div>
    
    <VisualizarAdjuntoComponent></VisualizarAdjuntoComponent>
  </div>
</template>

<script>
import Filters from '@/modules/Shared/Filters'
import VisualizarAdjuntoComponent from '@/modules/Tickets/components/VisualizarTicket/VisualizarAdjuntoComponent.vue';
import { socket } from '@/ws-client';
import { mapState, mapActions, mapMutations } from 'vuex'

export default {
    mixins: [Filters],
    components: { VisualizarAdjuntoComponent },
    props:
    {
        idticket: {
            type: Number
        }
    },
    data() {
        return {

        }
    },

    computed: {
        ...mapState(['user']),
        ...mapState('answers', ['answers', 'loadingAnswers']),
        ...mapState('tickets', ['ticket'])
    },

    methods: {
        ...mapActions('answers', ['getAllAnswers', 'deleteAnswer']),
        ...mapMutations('answers', ['resetListAnswers']),
        ...mapMutations('tickets', ['switchDialogFileAdjunto', 'setUrlFile', 'resetUrlFile']),
        
        onOpenDialogAdjunto(file) {
            this.resetUrlFile()
            this.switchDialogFileAdjunto()
            this.setUrlFile(file)
        },

        isMyID(iduser) {
            return this.user && this.user.iduser === iduser;
        }
    },
    created() {
        this.resetListAnswers()
    },
    mounted() {
        this.resetUrlFile()
        this.resetListAnswers() 
        this.getAllAnswers({ loading: true, idticket: this.ticket.idticket });

        socket.on('LOAD_ANSWER', data => {
            this.resetListAnswers()
            this.getAllAnswers({ loading: data, idticket: this.idticket });
        })
    },
}
</script>

<style scoped>
.answers-container {
  width: 100%;
}

.body-comment-ticket img {
    max-width: 100% !important;
    border-radius: 8px;
}

/* Avatar wrapper - Responsive margin */
.avatar-wrapper {
  margin-right: 16px;
}

@media (max-width: 600px) {
  .avatar-wrapper {
    margin-right: 10px;
  }
}

/* Línea conectora timeline */
.connector-line {
  position: absolute;
  left: 21px; 
  top: 42px;
  bottom: -24px;
  width: 2px;
  background-color: #EEEEEE;
  z-index: 0;
}

@media (max-width: 600px) {
  .connector-line {
    left: 16px;
    top: 32px;
  }
}

.z-index-1 {
  z-index: 1;
}

/* Indicador lateral de color */
.status-indicator {
  position: absolute;
  left: 0;
  top: 12px;
  bottom: 12px;
  width: 3px;
  border-radius: 0 4px 4px 0;
}

/* Tarjeta moderna: borde sutil + hover elegante */
.comment-card {
  background: #FFFFFF;
  border: 1px solid #EEF2F7;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  min-width: 0;
  padding: 16px;
}

@media (max-width: 600px) {
  .comment-card {
    border-radius: 12px;
    padding: 12px;
  }
}

.comment-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(16, 24, 40, 0.10);
  border-color: #E5E7EB;
}

.comment-card--mine {
  background: linear-gradient(180deg, rgba(0, 86, 145, 0.04) 0%, rgba(255, 255, 255, 1) 45%);
  border-color: rgba(0, 86, 145, 0.18);
}

.accent-bar {
  position: absolute;
  left: 14px;
  right: 14px;
  top: 10px;
  height: 2px;
  border-radius: 999px;
  opacity: 0.9;
}

@media (max-width: 600px) {
  .accent-bar {
    left: 10px;
    right: 10px;
    top: 8px;
  }
}

/* Header del comentario */
.comment-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 8px;
}

@media (max-width: 600px) {
  .comment-header {
    margin-bottom: 10px;
  }
}

.comment-header__user {
  flex: 1;
  min-width: 0;
}

.comment-header__name {
  font-size: 15px;
  font-weight: 700;
  color: #1F2937;
  display: inline-block;
}

@media (max-width: 600px) {
  .comment-header__name {
    font-size: 14px;
  }
}

.comment-header__email {
  font-size: 12px;
  color: #6B7280;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.comment-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.comment-header__date {
  font-size: 12px;
  color: #9CA3AF;
  white-space: nowrap;
}

@media (max-width: 600px) {
  .comment-header__date {
    font-size: 11px;
  }
}

.comment-header__delete {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.group:hover .comment-header__delete {
  opacity: 1;
}

@media (max-width: 600px) {
  .comment-header__delete {
    opacity: 1;
  }
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

/* Grid de Adjuntos */
.attachments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

@media (max-width: 600px) {
  .attachments-grid {
    grid-template-columns: 1fr;
  }
}

/* Tarjeta de Adjunto */
.attachment-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #FAFAFA 0%, #F5F5F5 100%);
  border: 1.5px solid #E8EBF0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.attachment-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #005691 0%, #002B49 100%);
  opacity: 0;
  transition: opacity 0.25s ease;
}

.attachment-card:hover {
  border-color: #005691;
  background: white;
  transform: translateY(-2px) translateX(2px);
  box-shadow: 0 8px 20px rgba(0, 86, 145, 0.15);
}

.attachment-card:hover::before {
  opacity: 1;
}

.attachment-card:active {
  transform: translateY(0) translateX(0);
}

.attachment-card__icon-wrapper {
  flex-shrink: 0;
}

.attachment-card__icon {
  background: white;
  border: 1px solid #E0E0E0;
  transition: all 0.25s ease;
}

.attachment-card:hover .attachment-card__icon {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 4px 12px rgba(0, 86, 145, 0.2);
}

.attachment-card__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.attachment-card__name {
  font-size: 13px;
  font-weight: 600;
  color: #212121;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.attachment-card__extension {
  font-size: 11px;
  font-weight: 700;
  color: #005691;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: rgba(0, 86, 145, 0.08);
  border-radius: 4px;
  width: fit-content;
}

.attachment-card__action {
  flex-shrink: 0;
  opacity: 0;
  transform: translateX(-8px);
  transition: all 0.25s ease;
}

.attachment-card:hover .attachment-card__action {
  opacity: 1;
  transform: translateX(0);
}

/* CC: pills tipo “recipients” */
.cc-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(0, 86, 145, 0.08);
  color: #0F172A;
  border: 1px solid rgba(0, 86, 145, 0.12);
  max-width: 100%;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.cc-pill:hover {
  background: rgba(0, 86, 145, 0.12);
  border-color: rgba(0, 86, 145, 0.18);
}

.caja-texto-styles {
    color: #424242;
}
</style>